import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import 'rxjs/add/operator/map';
import { Observable } from 'rxjs/Observable';
import { ConfigurationService } from './configuration.service';
import { EndpointFactory } from './endpoint-factory.service';

@Injectable()
export class AccountEndpoint extends EndpointFactory {
    private readonly _usersUrl: string = "/api/account/users";
    private readonly _publicusersUrl: string = "/api/account/publicusers";
    private readonly _adduserUrl: string = "/api/account/AddUser";
    private readonly _userByUserNameUrl: string = "/api/account/users/username";
    private readonly _currentUserUrl: string = "/api/account/users/me";
    private readonly _currentUserPreferencesUrl: string = "/api/account/users/me/preferences";
    private readonly _unblockUserUrl: string = "/api/account/users/unblock";
    private readonly _rolesUrl: string = "/api/account/roles";
    private readonly _getDefaultApplicationRoleNamesUrl: string = "/api/account/GetDefaultApplicationRoleNames";
    private readonly _roleByRoleNameUrl: string = "/api/account/roles/name";
    private readonly _permissionsUrl: string = "/api/account/permissions";
    private readonly _appVersionUrl: string = "/api/account/appVersion";
    private readonly _appVersionNameUrl: string = "/api/account/appVersionName";
    private readonly _appTrialDays: string = "/api/account/apptrialDays";
    private readonly _dashboardDates: string = "/api/account/GetDashboardDates";
    private readonly _sendPassordResetlinkUrl: string = "/api/account/sendPasswordResetlink";
    private readonly _resetPasswordUrl: string = "/api/account/users/resetPassword";
    private readonly _verifyChangePasswordIdentityUrl: string = "/api/account/users/VerifyChangePasswordIdentity";
    private readonly _markasDeleteUrl: string = "/api/account/MarkAsDelete";
    private readonly _updatePasswordUrl: string = "/api/account/UpdatePassword";
    private readonly _extendExpireUrl: string = "/api/account/ExtendExpiry";

    get extendExpireUrl() { return this.configurations.baseUrl + this._extendExpireUrl; }
    get markasDeleteUrl() { return this.configurations.baseUrl + this._markasDeleteUrl; }
    get usersUrl() { return this.configurations.baseUrl + this._usersUrl; }
    get publicusersUrl() { return this.configurations.baseUrl + this._publicusersUrl; }
    get adduserUrl() { return this.configurations.baseUrl + this._adduserUrl; }
    get userByUserNameUrl() { return this.configurations.baseUrl + this._userByUserNameUrl; }
    get currentUserUrl() { return this.configurations.baseUrl + this._currentUserUrl; }
    get currentUserPreferencesUrl() { return this.configurations.baseUrl + this._currentUserPreferencesUrl; }
    get unblockUserUrl() { return this.configurations.baseUrl + this._unblockUserUrl; }
    get rolesUrl() { return this.configurations.baseUrl + this._rolesUrl; }
    get getDefaultApplicationRoleNamesUrl() { return this.configurations.baseUrl + this._getDefaultApplicationRoleNamesUrl; }
    get roleByRoleNameUrl() { return this.configurations.baseUrl + this._roleByRoleNameUrl; }
    get permissionsUrl() { return this.configurations.baseUrl + this._permissionsUrl; }
    get appVersionUrl() { return this.configurations.baseUrl + this._appVersionUrl; }
    get appVersionNameUrl() { return this.configurations.baseUrl + this._appVersionNameUrl; }
    get appTrialDays() { return this.configurations.baseUrl + this._appTrialDays; }
    get dashboardDates() { return this.configurations.baseUrl + this._dashboardDates; }
    get sendPassordResetlinkUrl() { return this.configurations.baseUrl + this._sendPassordResetlinkUrl; }
    get resetPasswordUrl() { return this.configurations.baseUrl + this._resetPasswordUrl; }
    get verifyChangePasswordIdentityUrl() { return this.configurations.baseUrl + this._verifyChangePasswordIdentityUrl; }
    get updatePasswordUrl() { return this.configurations.baseUrl + this._updatePasswordUrl; }

    constructor(http: HttpClient, configurations: ConfigurationService, injector: Injector) {
        super(http, configurations, injector);
    }

    getUserExpireExtendEndpoint<T>(userId?: string, years?: number, date?: string): Observable<Response> {
        const endpointUrl = userId && years && date ? `${this.extendExpireUrl}/${userId}/${years}/${date}` : this.currentUserUrl;
        return this.http.get<T>(endpointUrl, this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getUserExpireExtendEndpoint(userId, years));
            });
    }

    getMarkAsDeleteEndpoint<T>(userId?: string): Observable<Response> {
        const endpointUrl = userId ? `${this.markasDeleteUrl}/${userId}` : this.currentUserUrl;

        return this.http.get<T>(endpointUrl, this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getMarkAsDeleteEndpoint(userId));
            });
    }

    sendPasswordResetlink(email: string) {
        let endpointUrl = email ? `${this.sendPassordResetlinkUrl}/${email}` : this.currentUserUrl;

        return this.http.get(endpointUrl).map(res => {
            return res;

        })
            .catch(error => {

                return this.handleError(error, () => this.sendPasswordResetlink(email));

            });
    }

    verifyChangePasswordIdentity(id: string, passwordVerificationKey: string) {
        const endpointUrl = id && passwordVerificationKey ? `${this.verifyChangePasswordIdentityUrl}/${id}/${passwordVerificationKey}` : '';
        return this.http.get(endpointUrl).map((response: Response) => {
            return response;
        });
    }

    resetPassword(id: string, newPassword: string) {
        let endpointUrl = id && newPassword ? `${this.resetPasswordUrl}/${id}/${newPassword}` : this.currentUserUrl;
        return this.http.get(endpointUrl).map(res => {
            return res;
        }).catch(error => {
            return this.handleError(error, () => this.resetPassword(id, newPassword));

        });
    }

    getUserUpdatePasswordEndpoint(userId?: string, newPassword?: string): Observable<Response> {
        let endpointUrl = userId && newPassword ? `${this.updatePasswordUrl}/${userId}/${newPassword}` : this.currentUserUrl;
        return this.http.get(endpointUrl, this.getRequestHeaders())
            .map((response: Response) => {
                return response;
            })
            .catch(error => {
                return this.handleError(error, () => this.getUserUpdatePasswordEndpoint(userId, newPassword));
            });
    }

    getApplicationVersion() {
        let endpointUrl = `${this.appVersionUrl}`;
        return this.http.get(endpointUrl, { responseType: "text" }).map((response: string) => {
            return response;
        });
    }

    getApplicationVersionName() {
        let endpointUrl = `${this.appVersionNameUrl}`;
        return this.http.get(endpointUrl, { responseType: "text" }).map((response: string) => {
            return response;
        });
    }

    getAppTrialDays() {
        let endpointUrl = `${this.appTrialDays}`;
        return this.http.get(endpointUrl, { responseType: "text" }).map((response) => {
            return response;
        });
    }

    getDashboardDates<T>(): Observable<T> {
        const endpointUrl = `${this.dashboardDates}`;

        return this.http.get<T>(endpointUrl, this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getDashboardDates());
            });
    }

    getUserEndpoint<T>(userId?: string): Observable<T> {
        let endpointUrl = userId ? `${this.usersUrl}/${userId}` : this.currentUserUrl;

        return this.http.get<T>(endpointUrl, this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getUserEndpoint(userId));
            });
    }

    getUserByUserNameEndpoint<T>(userName: string): Observable<T> {
        let endpointUrl = `${this.userByUserNameUrl}/${userName}`;
        return this.http.get<T>(endpointUrl, this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getUserByUserNameEndpoint(userName));
            });
    }

    getUsersEndpoint<T>(page?: number, pageSize?: number): Observable<T> {
        let endpointUrl = page && pageSize ? `${this.usersUrl}/${page}/${pageSize}` : this.usersUrl;

        return this.http.get<T>(endpointUrl, this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getUsersEndpoint(page, pageSize));
            });
    }

    getNewUserEndpoint<T>(userObject: any): Observable<T> {
        return this.http.post<T>(this.usersUrl, JSON.stringify(userObject), this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getNewUserEndpoint(userObject));
            });
    }

    getPublicNewUserEndpoint<T>(userObject: any): Observable<T> {
        return this.http.post<T>(this.publicusersUrl, JSON.stringify(userObject), this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getPublicNewUserEndpoint(userObject));
            });
    }

    getAddUserEndpoint<T>(userObject: any): Observable<T> {
        return this.http.post<T>(this.adduserUrl, JSON.stringify(userObject), this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getAddUserEndpoint(userObject));
            });
    }

    getUpdateUserEndpoint<T>(userObject: any, userId?: string): Observable<T> {
        let dexpireformat = "";
        if (userObject != null && userObject.ExpireDate != null) {
            userObject.ExpireDate = new Date(userObject.ExpireDate);
            dexpireformat = [userObject.ExpireDate.getDate(),
            userObject.ExpireDate.getMonth() + 1,
            userObject.ExpireDate.getFullYear()].join('-');
        }
        const endpointUrl = userId ? `${this.usersUrl}/${userId}/${dexpireformat}` : this.currentUserUrl;
        return this.http.put<T>(endpointUrl, JSON.stringify(userObject), this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getUpdateUserEndpoint(userObject, userId));
            });
    }

    getPatchUpdateUserEndpoint<T>(patch: {}, userId?: string): Observable<T>
    getPatchUpdateUserEndpoint<T>(value: any, op: string, path: string, from?: any, userId?: string): Observable<T>
    getPatchUpdateUserEndpoint<T>(valueOrPatch: any, opOrUserId?: string, path?: string, from?: any, userId?: string): Observable<T> {
        let endpointUrl: string;
        let patchDocument: {};

        if (path) {
            endpointUrl = userId ? `${this.usersUrl}/${userId}` : this.currentUserUrl;
            patchDocument = from ?
                [{ "value": valueOrPatch, "path": path, "op": opOrUserId, "from": from }] :
                [{ "value": valueOrPatch, "path": path, "op": opOrUserId }];
        } else {
            endpointUrl = opOrUserId ? `${this.usersUrl}/${opOrUserId}` : this.currentUserUrl;
            patchDocument = valueOrPatch;
        }
        return this.http.patch<T>(endpointUrl, JSON.stringify(patchDocument), this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getPatchUpdateUserEndpoint(valueOrPatch, opOrUserId, path, from, userId));
            });
    }

    getUserPreferencesEndpoint<T>(): Observable<T> {

        return this.http.get<T>(this.currentUserPreferencesUrl, this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getUserPreferencesEndpoint());
            });
    }

    getUpdateUserPreferencesEndpoint<T>(configuration: string): Observable<T> {
        return this.http.put<T>(this.currentUserPreferencesUrl, JSON.stringify(configuration), this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getUpdateUserPreferencesEndpoint(configuration));
            });
    }

    getUnblockUserEndpoint<T>(userId: string): Observable<T> {
        let endpointUrl = `${this.unblockUserUrl}/${userId}`;

        return this.http.put<T>(endpointUrl, null, this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getUnblockUserEndpoint(userId));
            });
    }

    getDeleteUserEndpoint<T>(userId: string): Observable<T> {
        let endpointUrl = `${this.usersUrl}/${userId}`;

        return this.http.delete<T>(endpointUrl, this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getDeleteUserEndpoint(userId));
            });
    }

    getRoleEndpoint<T>(roleId: string): Observable<T> {
        let endpointUrl = `${this.rolesUrl}/${roleId}`;

        return this.http.get<T>(endpointUrl, this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getRoleEndpoint(roleId));
            });
    }

    getRoleByRoleNameEndpoint<T>(roleName: string): Observable<T> {
        let endpointUrl = `${this.roleByRoleNameUrl}/${roleName}`;

        return this.http.get<T>(endpointUrl, this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getRoleByRoleNameEndpoint(roleName));
            });
    }

    getRolesEndpoint<T>(page?: number, pageSize?: number): Observable<T> {
        let endpointUrl = page && pageSize ? `${this.rolesUrl}/${page}/${pageSize}` : this.rolesUrl;

        return this.http.get<T>(endpointUrl, this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getRolesEndpoint(page, pageSize));
            });
    }

    getNewRoleEndpoint<T>(roleObject: any): Observable<T> {

        return this.http.post<T>(this.rolesUrl, JSON.stringify(roleObject), this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getNewRoleEndpoint(roleObject));
            });
    }

    getUpdateRoleEndpoint<T>(roleObject: any, roleId: string): Observable<T> {
        let endpointUrl = `${this.rolesUrl}/${roleId}`;

        return this.http.put<T>(endpointUrl, JSON.stringify(roleObject), this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getUpdateRoleEndpoint(roleObject, roleId));
            });
    }

    getDeleteRoleEndpoint<T>(roleId: string): Observable<T> {
        let endpointUrl = `${this.rolesUrl}/${roleId}`;

        return this.http.delete<T>(endpointUrl, this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getDeleteRoleEndpoint(roleId));
            });
    }

    getPermissionsEndpoint<T>(): Observable<T> {

        return this.http.get<T>(this.permissionsUrl, this.getRequestHeaders())
            .catch(error => {
                return this.handleError(error, () => this.getPermissionsEndpoint());
            });
    }

    getRolesEndPoint<T>(): Observable<T> {
        return this.http.get<T>(this.rolesUrl, this.getRequestHeaders());
    }

    getDefaultApplicationRoleNamesEndPoint<T>(): Observable<T> {
        return this.http.get<T>(this.getDefaultApplicationRoleNamesUrl, this.getRequestHeaders());
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./ClientApp/app/services/account-endpoint.service.ts